import React, { useCallback, useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
// components
import StaffItem from "../StaffItem";
import StaffItemMobile from "../StaffItemMobile";
import { SortArrows } from "../SortArrows";
import SecondaryButton from "../SecondaryButton";
import { useViewport } from "../ViewportProvider";
// icons
import { UpArrow, DownArrow } from "../../Styles/Icons/DesignSystem";
// services
import { getUserProperties } from "@intelligentlilli/api-layer";
import { mobileBreakpoint } from "../../Services/config";
import { updateStaffMember } from "../../State/slices/staff-slice";
import { capitalize } from "@intelligentlilli/lilli-utils";
import {
  getSentenceSeperator,
  sortByLastSeen,
  sortByResidentCount,
  sortByUserName,
} from "../../Services/Utils";
import { downloadCSV } from "../../Services/Utils/downloadCSV";
import { useGetUserRole } from "Services/Hooks";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";

const DashboardStaff = ({ searchValue }) => {
  const [filteredStaff, setFilteredStaff] = useState([]);
  // Sort state: only one can be active at a time. undefined = unsorted.
  const [sortUser, setSortUser] = useState(
    /** @type {boolean | undefined} */ (undefined)
  );
  const [sortResidents, setSortResidents] = useState(
    /** @type {boolean | undefined} */ (undefined)
  );
  const [sortLastSeen, setSortLastSeen] = useState(
    /** @type {boolean | undefined} */ (undefined)
  );
  const track = useAmplitudeTracking();

  const dispatch = useDispatch();
  const staff = useSelector((state) => state.staff);
  const serviceUsers = useSelector((state) => state.serviceUsers);
  const user = useSelector((state) => state.user.authData);

  // User's permissions (whether or not they can remove SUs from staff)
  const { userIsManagerOrAdmin } = useGetUserRole();

  const server = useSelector((state) => state.session.server);
  const navigate = useNavigate();
  const { width } = useViewport();
  const onMobile = width < mobileBreakpoint;

  const staffIdsString = JSON.stringify(staff.map((staff) => staff.id));

  const augmentedStaff = staff
    ?.map((staff) => {
      const augmentedRoles = staff.roles
        .map((role) => {
          switch (role) {
            case "app":
              return "carer";
            case "user":
              return "carer";
            case "manager":
              return "manager";
            case "installer":
              return "installer";
            case "friend-or-family":
              return "Friends & family";
            default:
              return "unknown";
          }
        })
        .filter((role) => role !== "Unknown");

      const uniqueAugmentedRoles = [...new Set(augmentedRoles)];
      const roleAsString = getSentenceSeperator(uniqueAugmentedRoles, "and");

      // is this you
      const thisIsYou = staff?.id === user?.key;

      // filter out inactive hubs since the organisation/{id}/staff endpoint returns inactive and active hubs
      const activeHubCount = staff?.hubs?.filter((hub) => hub?.active === 1);

      // carers/staff/installersonly should only see hubs they share with others.
      // In other words, if User A and User B are both linked to the same SU,
      // then User A should see that SU listed under User B as well.
      const overlappingHubs = activeHubCount?.filter((staffHub) => {
        return serviceUsers?.find((su) => su?.id === staffHub?.id);
      });

      const hubs =
        userIsManagerOrAdmin || thisIsYou ? activeHubCount : overlappingHubs;

      return {
        ...staff,
        roles: uniqueAugmentedRoles.map((role) =>
          role ? capitalize(role) : null
        ),
        rolesString: roleAsString ? capitalize(roleAsString) : null,
        serviceUsersString: `${hubs.length ?? 0} resident${
          hubs.length === 1 ? "" : "s"
        }`,
        visibleHubs:
          userIsManagerOrAdmin || thisIsYou ? activeHubCount : overlappingHubs,
      };
    })
    .filter((staff) => staff.rolesString !== "Friends & family");

  const augmentedStaffString = JSON.stringify(augmentedStaff);

  useEffect(() => {
    const staffToFilter = JSON.parse(augmentedStaffString);
    if (searchValue) {
      const filteredStaff = staffToFilter.filter((staff) => {
        // check whether the surname matches the search value
        const surnameMatch =
          staff.surname?.slice(0, searchValue.length).toLowerCase() ===
          searchValue.toLowerCase();
        const firstNameMatch =
          staff.forenames?.slice(0, searchValue.length).toLowerCase() ===
          searchValue.toLowerCase();
        return surnameMatch || firstNameMatch;
      });
      setFilteredStaff(filteredStaff);
    } else {
      setFilteredStaff(staffToFilter);
    }
  }, [searchValue, augmentedStaffString]);

  // Get request for the staff members data and photo
  useEffect(() => {
    async function fetchData() {
      const staffIds = JSON.parse(staffIdsString) || [];
      // Wait for both the getUser and getUserHubs API calls to finish
      await Promise.all(
        staffIds.map((id, index) =>
          getUserProperties(server, id, "web").then(({ data }) => {
            dispatch(
              updateStaffMember({
                index,
                key: "lastActivity",
                value: data?.lastActivity,
              })
            );
          })
        )
      );
    }
    fetchData();
  }, [server, staffIdsString, dispatch]);

  const getSortedStaff = () => {
    if (sortUser !== undefined) return sortByUserName(filteredStaff, sortUser);
    if (sortResidents !== undefined)
      return sortByResidentCount(filteredStaff, sortResidents);
    if (sortLastSeen !== undefined)
      return sortByLastSeen(filteredStaff, sortLastSeen);
    return filteredStaff;
  };
  const sortedFilteredStaff = getSortedStaff();

  const handleDownloadCSV = useCallback(() => {
    track("CSV Download", { segment: "staff_list" });
    const headers = ["Name", "Role", "Residents", "Last Seen"];
    const rows = sortedFilteredStaff?.map((s) => [
      `${s.forenames ?? ""} ${s.surname ?? ""}`.trim(),
      s.rolesString ?? "",
      s.serviceUsersString ?? "",
      s.lastActivity
        ? new Date(
            Number(String(s.lastActivity).slice(0, -3)) * 1000
          ).toLocaleDateString()
        : "Unknown",
    ]);
    downloadCSV(headers, rows, "staff-export.csv");
  }, [sortedFilteredStaff, track]);

  return (
    <>
      {!onMobile && (
        <div className={styles.export_row}>
          <SecondaryButton onClick={handleDownloadCSV}>
            Download CSV
          </SecondaryButton>
        </div>
      )}
      {!onMobile && (
        <div className={styles.table}>
          <div className={styles.table_header}>
            <div
              className={styles.sortable_header}
              onClick={() => {
                setSortResidents(undefined);
                setSortLastSeen(undefined);
                setSortUser(sortUser === true ? false : true);
              }}
            >
              <span>User</span>
              {sortUser === true ? (
                <DownArrow />
              ) : sortUser === false ? (
                <UpArrow />
              ) : (
                <SortArrows />
              )}
            </div>
            <div>Role</div>
            <div
              className={styles.sortable_header}
              onClick={() => {
                setSortUser(undefined);
                setSortLastSeen(undefined);
                setSortResidents(sortResidents === true ? false : true);
              }}
            >
              <span>Residents</span>
              {sortResidents === true ? (
                <DownArrow />
              ) : sortResidents === false ? (
                <UpArrow />
              ) : (
                <SortArrows />
              )}
            </div>
            <div
              className={styles.sortable_header}
              onClick={() => {
                setSortUser(undefined);
                setSortResidents(undefined);
                setSortLastSeen(sortLastSeen === true ? false : true);
              }}
            >
              <span>Last seen</span>
              {sortLastSeen === true ? (
                <DownArrow />
              ) : sortLastSeen === false ? (
                <UpArrow />
              ) : (
                <SortArrows />
              )}
            </div>
          </div>
          {sortedFilteredStaff.map((staff, index) => (
            <StaffItem key={index} staff={staff} navigate={navigate} />
          ))}
        </div>
      )}
      {onMobile &&
        sortedFilteredStaff.map((staff, index) => (
          <StaffItemMobile key={index} staff={staff} navigate={navigate} />
        ))}
    </>
  );
};

export default DashboardStaff;
